// DEMO BACKEND — the onboarding walkthrough runs the REAL ThinkingWorkspace against a session id that exists nowhere on the server.
// While the demo is active, every request for `/api/ot/demo-onboarding/…` is answered HERE (an axios per-request adapter) from an in-memory
// store seeded with fixtures the backend's own deterministic derivations produced offline (tests/build_onboarding_fixtures.py). Nothing is sent,
// nothing is written, no controller runs, no LLM: coach replies are the approved script. Stopping the demo discards the store.
import axios from "axios";
import fixtures from "./demoFixtures.json";

export const DEMO_SESSION_ID = "demo-onboarding";
export const DEMO = {
  assignment: fixtures.assignment,
  sentence: fixtures.sentence,
  coachOpener: fixtures.coach_opener,
  reply1: "People keep checking notifications and thinking about their phones.",
  reply2: "Not exactly. I also think phones change how students interact with each other.",
  wroteEvent: "the student wrote the next paragraph of their essay",
};
// the approved deterministic script — keyed by what the student did / said
export const COACH_SCRIPT = [
  { event: DEMO.wroteEvent, reply: "You have a position and one reason for it. When you say phones make it harder to pay attention, what do you think is happening?" },
  { message: DEMO.reply1, reply: "That helps me see it. It sounds like your main concern is distraction." },
  { message: DEMO.reply2, reply: "That helps. So distraction may be only one part of your argument." },
];
export const COACH_FALLBACK = "In this demonstration my replies are scripted. In your real session I read what you actually write and respond to it.";

const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();
let store = null;
export const demoLog = [];                                   // {method, path} of every answered call (tests / Test Mode inspection)

const fresh = () => ({ state: "s0", dialogue: JSON.parse(JSON.stringify(fixtures.states.s0.ot.dialogue)), rev: 1, turns: 0, closedKeys: [] });
const current = () => fixtures.states[store.state];
const otNow = () => ({ ...JSON.parse(JSON.stringify(current().ot)), dialogue: store.dialogue.slice(), rev: store.rev });
const payload = () => { const s = current(); return { ot: otNow(), sufficiency: s.sufficiency, structure: s.structure, document: s.document }; };

export const isDemoActive = () => !!store;
export const demoState = () => (store ? store.state : null);
export const demoDialogue = () => (store ? store.dialogue.slice() : []);
export function startDemo() { store = fresh(); demoLog.length = 0; }
export function stopDemo() { store = null; }

function scriptedReply(body) {
  const hit = COACH_SCRIPT.find((c) => (c.event && norm(c.event) === norm(body.event)) || (c.message && norm(c.message) === norm(body.message)));
  return hit ? hit.reply : COACH_FALLBACK;
}

function route(method, path, body) {
  if (!store) throw new Error("demo backend inactive");
  demoLog.push({ method, path });
  const m = `${method} ${path}`;
  if (m === "GET /ws") return payload();
  if (m === "POST /ws/item") {
    store.state = "s1"; store.rev += 1;
    const ot = otNow();
    return { item: ot.workspace.items[0], ot };
  }
  if (m === "POST /ws/coach") {
    store.turns += 1;
    if (body.message) store.dialogue.push({ role: "student", text: body.message, focus: [], turn_id: `demo-s${store.turns}` });
    const reply = scriptedReply(body || {});
    store.dialogue.push({ role: "tws", text: reply, focus: [], turn_id: `demo-t${store.turns}` });
    const s = current();
    return { message: reply, focus_item_ids: [], focus_question_ids: [], turn_id: `demo-t${store.turns}`, ot: otNow(), document: s.document, structure: s.structure,
             awaiting_operation: null, thesis_proposal: null, region_offer: null, latent_structure: null, active_field: null, dev_op: null, timing: {}, compose_turn: null,
             captured: [], developments: [], consolidated: { need: "develop_part", locus: "part", whole_state: "usable", capability: "elaboration", support_level: 1, operation: "ELABORATE",
             structure_visible: true, whole_orientation: { level: 0, reason: "demo", key: "", message: "" }, recorded_as: body.message ? "dialogue" : "writing" } };
  }
  if (m === "GET /ws/emerging-whole") return current().whole;
  if (m === "GET /ws/coverage") return current().coverage;
  if (/^GET \/ws\/turn\//.test(m)) return { status: "not_run" };
  if (m === "POST /ws/big-picture/close") { store.closedKeys.push((body || {}).key || ""); return { ok: true }; }
  if (m === "POST /ws/rep-status") return { ot: otNow(), rep_status: (body || {}).status || "" };
  if (/compose/.test(path)) return { ot: otNow(), unit: null, thread: [], focus: null };
  if (method === "PATCH" || method === "POST" || method === "DELETE") return { ot: otNow() };
  return {};
}

const DEMO_PATH = new RegExp(`/ot/${DEMO_SESSION_ID}(/.*)?$`);
export function demoAdapter(config) {
  return new Promise((resolve, reject) => {
    try {
      const url = (config.url || "").split("?")[0];
      const path = (url.match(DEMO_PATH) || [])[1] || "/";
      let body = config.data;
      if (typeof body === "string") { try { body = JSON.parse(body); } catch { body = {}; } }
      const data = route((config.method || "get").toUpperCase(), path, body || {});
      setTimeout(() => resolve({ data, status: 200, statusText: "OK", headers: {}, config, request: { demo: true } }), 30);
    } catch (e) { reject(e); }
  });
}
// installed once at import: only a demo-session URL while the demo is active is redirected — every other request is untouched
let installed = false;
export function installDemoInterceptor() {
  if (installed) return;
  installed = true;
  axios.interceptors.request.use((config) => {
    if (store && typeof config.url === "string" && DEMO_PATH.test(config.url.split("?")[0])) config.adapter = demoAdapter;
    return config;
  });
}
installDemoInterceptor();
export const demoInitialOt = () => JSON.parse(JSON.stringify(fixtures.states.s0.ot));
